import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Badge,
  Card,
  DataTable,
  EmptyState,
  FilterBar,
  FormField,
  LoadingState,
  Select,
} from '@sakhtar/design-system/react';
import { listUpcomingBirthdays, type UpcomingBirthday } from '../../lib/operatorApi.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import { faDigits } from '../../lib/faDigits.js';
import { birthdayDayMonth, untilLabel } from './birthdaysModel.js';
import { OperatorPage } from './OperatorShell.js';
import { BrandColorChip, OrgAvatar, OrgUserAvatar, WithAvatar } from './OperatorAvatars.js';

const WINDOWS = [
  { value: '7', label: '۷ روز آینده' },
  { value: '30', label: '۳۰ روز آینده' },
  { value: '90', label: '۹۰ روز آینده' },
  { value: '365', label: 'یک سال آینده' },
] as const;

/**
 * Upcoming birthdays of every member of every active organization, soonest
 * first, with the person's own avatar, their organization's logo and the color
 * they picked. Reading this is audited; it needs the all-organizations scope.
 */
export function BirthdaysPage() {
  const [days, setDays] = useState('30');
  const [rows, setRows] = useState<readonly UpcomingBirthday[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setError(null);
    void listUpcomingBirthdays(Number(days))
      .then((list) => {
        if (!cancelled) setRows(list);
      })
      .catch(() => {
        if (!cancelled) setError('بارگذاری تولدها ناموفق بود یا دسترسی لازم را ندارید.');
      });
    return () => {
      cancelled = true;
    };
  }, [days]);

  const stats = useMemo(() => {
    const list = rows ?? [];
    return {
      today: list.filter((r) => r.daysUntil === 0).length,
      week: list.filter((r) => r.daysUntil <= 7).length,
      total: list.length,
    };
  }, [rows]);

  return (
    <OperatorPage title="تولدهای نزدیک">
      <FilterBar>
        <FormField label="بازه">
          <Select value={days} onChange={(e) => setDays(e.target.value)} aria-label="بازهٔ نمایش">
            {WINDOWS.map((w) => (
              <option key={w.value} value={w.value}>
                {w.label}
              </option>
            ))}
          </Select>
        </FormField>
      </FilterBar>

      {error ? <Alert tone="danger">{error}</Alert> : null}
      {rows === null && !error ? <LoadingState /> : null}

      {rows ? (
        <>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Badge tone={stats.today > 0 ? 'success' : 'default'}>
              امروز: {faDigits(stats.today)}
            </Badge>
            <Badge tone="info">تا ۷ روز آینده: {faDigits(stats.week)}</Badge>
            <Badge tone="default">در این بازه: {faDigits(stats.total)}</Badge>
          </div>
          {rows.length === 0 ? (
            <EmptyState title="در این بازه تولدی ثبت نشده است." />
          ) : (
            <Card>
              <DataTable
                columns={[
                  {
                    key: 'person',
                    header: 'کاربر',
                    render: (row: UpcomingBirthday) => (
                      <WithAvatar
                        avatar={
                          <OrgUserAvatar
                            slug={row.organizationRef}
                            accountId={row.accountId}
                            name={row.displayName}
                            preset={row.avatarPreset}
                            hasPhoto={row.hasAvatarPhoto}
                            size={34}
                          />
                        }
                      >
                        <strong>{row.displayName}</strong>
                        <div style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>
                          {row.roleTitle}
                        </div>
                      </WithAvatar>
                    ),
                  },
                  {
                    key: 'org',
                    header: 'سازمان',
                    render: (row: UpcomingBirthday) => (
                      <WithAvatar
                        avatar={
                          <OrgAvatar slug={row.organizationRef} name={row.organizationName} />
                        }
                      >
                        <Link
                          to={`/internal/admin/organizations/${encodeURIComponent(row.organizationRef)}`}
                        >
                          {row.organizationName}
                        </Link>
                      </WithAvatar>
                    ),
                  },
                  {
                    key: 'birthday',
                    header: 'روز تولد',
                    render: (row: UpcomingBirthday) => (
                      <div>
                        <strong>{birthdayDayMonth(row.birthdate)}</strong>
                        <div style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>
                          {faDigits(row.turningAge)} ساله می‌شود
                        </div>
                      </div>
                    ),
                  },
                  {
                    key: 'when',
                    header: 'چند روز مانده',
                    render: (row: UpcomingBirthday) => (
                      <Badge
                        tone={
                          row.daysUntil === 0 ? 'success' : row.daysUntil <= 7 ? 'info' : 'default'
                        }
                      >
                        {untilLabel(row.daysUntil)}
                      </Badge>
                    ),
                  },
                  {
                    key: 'date',
                    header: 'تاریخ تولد',
                    render: (row: UpcomingBirthday) => (
                      <span style={{ fontSize: 13 }}>{displayJalaliDate(row.birthdate)}</span>
                    ),
                  },
                  {
                    key: 'color',
                    header: 'رنگ انتخابی',
                    render: (row: UpcomingBirthday) => <BrandColorChip color={row.brandColor} />,
                  },
                ]}
                rows={[...rows]}
                rowKey={(row: UpcomingBirthday) => `${row.organizationRef}:${row.accountId}`}
              />
            </Card>
          )}
        </>
      ) : null}
    </OperatorPage>
  );
}
